import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { IEnvironment } from '../../models/environment.model';
import {
  INidVerificationConfig,
  INidVerificationResult,
  INidApiResponse,
  INidApiErrorBody,
  INidPrefetchProfileData,
  INidVerifyRequest,
  isUserRequireValidationResponse,
} from '../../models/generic-data-fetch-pre-pipeline.model';
import {
  CustomIdInputTypesApiUrls,
  EIdType,
  ENIDType,
  IIdInputTypeApiUrls,
  TIdType,
  TNIDType,
} from '../../config/custom-id-input-api-urls.config';
import { updateUrlWithApiGatewayBaseUrl } from '../../../utils/utils/data-fetch-widget-utils';
import { IHttpSingleDataResponse } from '../../../utils/models/http-single-data-response.model';

/** Result from the internal prefetch step */
interface INidPrefetchResult {
  skip: boolean;
  passed: boolean;
  citizenId?: string;
}

/** NID verification error keys */
export enum ENidVerificationErrorKeys {
  VERIFICATION_FAILED = 'verificationFailed',
  VERIFICATION_INVALID = 'verificationInvalid',
  VERIFICATION_REQUIRED = 'verificationRequired',
}

/** NID verification methods */
export enum ENidVerificationMethods {
  NAME = 'NAME',
  DATE_OF_BIRTH = 'DATE_OF_BIRTH',
}

/**
 * Encapsulates the NID ownership verification flow:
 * prefetch → privacy check → verify modal → verify endpoint.
 *
 * Usage:
 * ```html
 * <irembogov-nid-verification
 *   #nidVerification
 *   [modalContainerId]="'validate-id-fetch-modal-container-' + field.key">
 * </irembogov-nid-verification>
 * ```
 *
 * Parent calls: `await this.nidVerification.verify(config, citizenId, resolvedNidType)`
 */
@Component({
  selector: 'irembogov-nid-verification',
  templateUrl: './nid-verification.component.html',
  styleUrls: ['./nid-verification.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NidVerificationComponent {
  @ViewChild('nidVerificationModal')
  nidVerificationModal!: TemplateRef<any>;

  /** CSS selector for the modal container element (on the parent's template) */
  @Input() modalContainerId?: string;

  // ── Modal state ──
  ENidVerificationMethods = ENidVerificationMethods;
  nidVerificationMethod?: ENidVerificationMethods;
  nidVerifyingIdValue?: string;
  isNidValidating = false;

  nidFirstOrLastName: FormControl = new FormControl('', [
    Validators.pattern(/^\S+(?:\s+\S+)*$/),
  ]);
  nidDateOfBirth: FormControl = new FormControl('');

  private environment: IEnvironment;

  constructor(
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    private modalService: NgbModal,
    private translateService: TranslateService,
    @Inject('environment') environment: IEnvironment
  ) {
    this.environment = environment;
  }

  /**
   * Runs the full NID verification flow.
   *
   * @param config - NID verification configuration from form JSON
   * @param citizenId - The current input value (NID number)
   * @param resolvedNidType - Optional NID sub-type resolved by the parent
   * @returns Result indicating pass/fail + optional overridden citizenId
   */
  async verify(
    config: INidVerificationConfig,
    citizenId: string,
    resolvedNidType?: TNIDType
  ): Promise<INidVerificationResult> {
    const idUrls = this.resolveIdUrls(config.idType);
    const endpointCode = config.endpointCode;

    // ── Prefetch from user profile ──
    if (
      config.usePrefetch &&
      idUrls.prefetchUrl &&
      idUrls.prefetchFieldKey &&
      !this.environment.applicationByOther
    ) {
      const prefetchResult = await this.nidPrefetch(
        idUrls,
        config,
        resolvedNidType
      );
      if (prefetchResult.skip) {
        return {
          passed: prefetchResult.passed,
          citizenId: prefetchResult.citizenId,
          errorKey: prefetchResult.passed
            ? undefined
            : ENidVerificationErrorKeys.VERIFICATION_FAILED,
        };
      }
      if (prefetchResult.citizenId) {
        citizenId = prefetchResult.citizenId;
      }
    }

    if (!idUrls.privacy) {
      return { passed: true, citizenId };
    }

    const privacyUrl = updateUrlWithApiGatewayBaseUrl(
      idUrls.privacy,
      this.environment
    );

    try {
      const privacyResponse = await lastValueFrom(
        this.http.get<INidApiResponse>(privacyUrl, {
          headers: {
            'Citizen-Id': citizenId,
            'Endpoint-Code': endpointCode ?? '',
          },
        })
      );

      if (isUserRequireValidationResponse(privacyResponse)) {
        return this.runVerifyModal(citizenId, idUrls, endpointCode, config);
      }

      return { passed: true, citizenId };
    } catch (httpError: unknown) {
      const errorBody = (httpError as HttpErrorResponse)
        ?.error as INidApiErrorBody;

      if (isUserRequireValidationResponse(errorBody)) {
        return this.runVerifyModal(citizenId, idUrls, endpointCode, config);
      }

      return {
        passed: false,
        errorKey: ENidVerificationErrorKeys.VERIFICATION_FAILED,
        errorMessage: this.translateService.instant(
          'ID verification failed. Please try again.'
        ),
      };
    }
  }

  /** Verified NID number from the user's profile, or null if not verified. */
  async prefetchVerifiedIdNumber(
    config: INidVerificationConfig,
    resolvedNidType?: TNIDType
  ): Promise<string | null> {
    const result = await this.nidPrefetch(
      this.resolveIdUrls(config.idType),
      config,
      resolvedNidType
    );
    return result.passed ? result.citizenId ?? null : null;
  }

  private async nidPrefetch(
    idUrls: IIdInputTypeApiUrls,
    config: INidVerificationConfig,
    resolvedNidType?: TNIDType
  ): Promise<INidPrefetchResult> {
    if (!idUrls.prefetchUrl) {
      return { skip: false, passed: false };
    }

    const prefetchUrl = updateUrlWithApiGatewayBaseUrl(
      idUrls.prefetchUrl,
      this.environment
    );

    try {
      const res = await lastValueFrom(
        this.http.get<IHttpSingleDataResponse<INidPrefetchProfileData>>(
          prefetchUrl
        )
      );

      const profileData = res.data;
      const isVerified: boolean =
        profileData?.accountVerified &&
        profileData?.identificationType === idUrls.prefetchFieldKey &&
        !!profileData?.identificationNumber;

      if (isVerified) {
        const identificationNumber = profileData.identificationNumber;

        if (config.idType === EIdType.NID && resolvedNidType) {
          const firstDigit = identificationNumber?.charAt(0);
          const typeMatch =
            (resolvedNidType === ENIDType.NATIONAL_ID && firstDigit === '1') ||
            (resolvedNidType === ENIDType.REFUGEE_ID && firstDigit === '2') ||
            (resolvedNidType === ENIDType.FOREIGNER_ID && firstDigit === '3');

          if (!typeMatch) {
            return { skip: false, passed: false };
          }
        }

        return { skip: false, passed: true, citizenId: identificationNumber };
      }

      return { skip: false, passed: false };
    } catch {
      return { skip: false, passed: false };
    }
  }

  private async runVerifyModal(
    citizenId: string,
    idUrls: IIdInputTypeApiUrls,
    endpointCode: string,
    config: INidVerificationConfig
  ): Promise<INidVerificationResult> {
    if (!idUrls.verify) {
      return {
        passed: false,
        errorKey: ENidVerificationErrorKeys.VERIFICATION_FAILED,
        errorMessage: this.translateService.instant(
          'Verify URL not configured for this ID type.'
        ),
      };
    }

    const verifyUrl = updateUrlWithApiGatewayBaseUrl(
      idUrls.verify,
      this.environment
    );

    // Randomly select verification method
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    const randomValue = arr[0] * Math.pow(2, -32);
    this.nidVerificationMethod =
      randomValue < 0.5
        ? ENidVerificationMethods.DATE_OF_BIRTH
        : ENidVerificationMethods.NAME;

    // Reset modal form controls
    this.nidFirstOrLastName.reset();
    this.nidDateOfBirth.reset();
    this.nidVerifyingIdValue = citizenId;
    this.isNidValidating = false;
    this.cd.detectChanges();

    try {
      const modalRef = this.modalService.open(this.nidVerificationModal, {
        ariaLabelledBy: 'modal-validate-id-fetch-title',
        centered: true,
        backdrop: 'static',
        modalDialogClass: 'modal-validate-id-fetch-dialog',
        windowClass: 'modal-validate-id-fetch-window',
        container: this.modalContainerId
          ? `#${this.modalContainerId}`
          : undefined,
      });

      const modalResult = await lastValueFrom(modalRef.closed);

      if (modalResult !== 'verifyId') {
        return {
          passed: false,
          errorKey: ENidVerificationErrorKeys.VERIFICATION_REQUIRED,
          errorMessage: this.translateService.instant(
            "Please verify ID owner's consent."
          ),
        };
      }

      // Validate modal inputs
      if (
        !(
          (this.nidDateOfBirth.value || this.nidFirstOrLastName.value) &&
          this.nidDateOfBirth.valid &&
          this.nidFirstOrLastName.valid
        )
      ) {
        this.nidVerifyingIdValue = undefined;
        return {
          passed: false,
          errorKey: ENidVerificationErrorKeys.VERIFICATION_REQUIRED,
          errorMessage: this.translateService.instant(
            'Fill the required verification information correctly.'
          ),
        };
      }

      // Build verify params
      const verifierValue =
        this.nidVerificationMethod === ENidVerificationMethods.DATE_OF_BIRTH
          ? this.nidDateOfBirth.value
          : this.nidVerificationMethod === ENidVerificationMethods.NAME
          ? this.nidFirstOrLastName.value
          : undefined;

      if (!this.nidVerificationMethod || !verifierValue) {
        return {
          passed: false,
          errorKey: ENidVerificationErrorKeys.VERIFICATION_FAILED,
          errorMessage: this.translateService.instant(
            'Invalid verification method.'
          ),
        };
      }

      const verifyParams: INidVerifyRequest = {
        ...(config.idType === EIdType.NID ? { nid: citizenId } : {}),
        ...(config.idType === EIdType.CHILD_ID ? { childId: citizenId } : {}),
        verifierType: this.nidVerificationMethod,
        verifierValue,
      };

      // Call verify endpoint
      this.isNidValidating = true;
      this.cd.detectChanges();

      try {
        const verifyResponse = await lastValueFrom(
          this.http.post<INidApiResponse>(verifyUrl, verifyParams, {
            headers: {
              'Citizen-Id': citizenId,
              'Endpoint-Code': endpointCode ?? '',
            },
          })
        );

        this.isNidValidating = false;
        this.nidVerifyingIdValue = undefined;

        if (isUserRequireValidationResponse(verifyResponse)) {
          return {
            passed: false,
            errorKey: ENidVerificationErrorKeys.VERIFICATION_INVALID,
            errorMessage: this.translateService.instant(
              "Details don't match records. Please check the I.D. owner's info and try again."
            ),
          };
        }

        return { passed: true, citizenId };
      } catch (verifyError: unknown) {
        this.isNidValidating = false;
        const verifyErrorBody = (verifyError as HttpErrorResponse)
          ?.error as INidApiErrorBody;

        if (isUserRequireValidationResponse(verifyErrorBody)) {
          return {
            passed: false,
            errorKey: ENidVerificationErrorKeys.VERIFICATION_INVALID,
            errorMessage: this.translateService.instant(
              "Details don't match records. Please check the I.D. owner's info and try again."
            ),
          };
        }

        return {
          passed: false,
          errorKey: ENidVerificationErrorKeys.VERIFICATION_FAILED,
          errorMessage: this.translateService.instant(
            'ID verification failed. Please try again.'
          ),
        };
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'EmptyError') {
        // Modal was dismissed without a result
      }
      return {
        passed: false,
        errorKey: ENidVerificationErrorKeys.VERIFICATION_REQUIRED,
        errorMessage: this.translateService.instant(
          "Please verify ID owner's consent."
        ),
      };
    }
  }

  private resolveIdUrls(idType: TIdType): IIdInputTypeApiUrls {
    const apiUrls = CustomIdInputTypesApiUrls();
    switch (idType) {
      case EIdType.NID:
        return apiUrls.nid;
      case EIdType.CHILD_ID:
        return apiUrls.childId;
      case EIdType.NIN:
        return apiUrls.nin;
      default:
        return apiUrls.nid;
    }
  }
}
